import { useContext, useEffect, useRef, useState } from "react";
import "./CheckOutPayment.css";
import { CardElement, ExpressCheckoutElement } from "@stripe/react-stripe-js";
import Button from "../../UI/Button/Button";
import {
  clearCart,
  setEntireCart,
  setOrderDetails,
  setPaymentSuccess
} from "../../state_management/actions";
import * as actions from "../../state_management/actionTypes";
import CheckOutListReschedule from "../CheckOutList/CheckOutListReschedule";
import { CheckOutSuccess } from "../../pages/CheckOut/CheckOut";
import { Raw } from "../../App";
import { CheckOutPaymentLeft } from "./CheckOutPaymentLeft";
import usePayment, { parseDirectOrder } from "./paymentHook";
import moment from "moment";
import { isDepositAvailable } from ".";
import { calc_price_with_discount, dateFormat } from "../../utilities/misc";
import { connect } from "react-redux";
import { orderType } from "../../state_management/reducers/order";
import { stateType } from "../../store";
import { initialState as defaultOrderDetails } from "../../state_management/reducers/order";
import { cartInitialState } from "../../state_management/reducers/cart";
import {
  epics_cart_items,
  only_gift,
  parseToCartItem
} from "../../utilities/cartUtilities";
import {
  CartTotalContext,
  LocaleContext,
  TotalWithCouponContext
} from "../../AppProvider";

/**
 *
 * What do I need
 * - Start and end dates
 * - Price at deposit and full payment but without the ability to change
 * - Client secret for making payments
 * - Get the wrapper element to wrap the payment form
 * - A list of the services, discount applied and total
 */

export const total = (coupTotal: number | null | undefined, total: number) => {
  const allowNum = typeof coupTotal === "number";

  if (allowNum) {
    return coupTotal;
  } else {
    return total;
  }
};

const PaymentDirect = ({
  directPaymentInfo
}: {
  directPaymentInfo: actions.orderDetailsType;
}) => {
  const [finished, setFinished] = useState(
    directPaymentInfo.data.status === "confirmed"
  );

  const id = directPaymentInfo.data.id;
  const customer_email = directPaymentInfo.data.customer.email;

  const reserved =
    directPaymentInfo.data.status === "pending" ||
    directPaymentInfo.data.status === "reserved";

  return reserved && !finished ? (
    <PaymentDirectForm
      id={id}
      setFinished={setFinished}
      directPaymentInfo={directPaymentInfo}
    />
  ) : directPaymentInfo.data.status === "ongoing" ? ( // will only reach here on reload and when the order is ongoin
    <CheckOutOngoing />
  ) : finished || directPaymentInfo.data.status === "confirmed" ? (
    <CheckOutSuccess customer_email={customer_email} /> // will reach here both on when the order is confirmed and when the payment is sent successfully for processing
  ) : (
    <></>
  );
};

export default PaymentDirect;

const PaymentDirectFormCon = ({
  id,
  directPaymentInfo,
  setFinished,
  details,
  setOrderDetails,
  clearCart,
  setEntireCart,
  cart
}: {
  id: number;
  directPaymentInfo: actions.orderDetailsType;
  setFinished: (data: boolean) => void;
  details: orderType;
  setOrderDetails: (details: orderType) => void;
  clearCart: (ignoreAnalytics: boolean) => void;
  setEntireCart: (items: cartInitialState) => void;
  cart: cartInitialState;
}) => {
  const { t, locale } = useContext(LocaleContext);
  const [cartTotal] = useContext(CartTotalContext);

  const [error, setError] = useState<string>();
  const [processing, setProcessing] = useState(false);
  const [disabled, setDisabled] = useState(true);

  const orderDetails = directPaymentInfo.data;
  const clientSecret = orderDetails.client_secret;

  const cartItems = orderDetails.services;
  const req_gift_values = orderDetails.gift_values || [];
  const extras = orderDetails.extra_services || [];

  const { extra_services, gift_values, services } = epics_cart_items({
    cart: cartItems,
    extras,
    gift_values: req_gift_values.map((a) => ({
      ...a,
      item: { price: a.value }
    }))
  });

  const parseCart = {
    extra_services,
    services
  };

  useEffect(() => {
    const details = parseDirectOrder(orderDetails);
    setOrderDetails({ ...details });
  }, [JSON.stringify(orderDetails)]);

  const {
    cart: cart_items,
    extras: extra_items,
    gift_services
  } = parseToCartItem({
    cart: cartItems,
    extras,
    gift_values: req_gift_values
  });
  useEffect(() => {
    setEntireCart({
      cart: cart_items.concat(gift_services),
      extras: extra_items
    });
  }, [JSON.stringify(orderDetails)]);

  useEffect(() => {
    return () => {
      setDefault();
    };
  }, []);

  const setDefault = () => {
    setOrderDetails({ ...defaultOrderDetails.details });
    clearCart(true);
  };

  const [totalWithCoupon] = useContext(TotalWithCouponContext);

  const usePay = usePayment({
    services: parseCart.services,
    details,
    gift_values: gift_values,
    extra_services: parseCart.extra_services,
    orderInfo: {
      cleaner_id: orderDetails.cleaner_id,
      clientSecret,
      orderId: orderDetails.id,
      status: orderDetails.status,
      random_cleaner: !!orderDetails.random_cleaner,
      time_start: moment(orderDetails.time_start).format("YYYY-MM-DD HH:mm")
    },
    setOrderInfo: () => {},
    isDirect: true,
    orderFailFunc: () => {},
    //Other
    clientSecret,
    Total: totalWithCoupon,
    setError,
    setDisabled,
    setProcessing,
    setPaymentSuccess,
    startSubmit: () => {
      setFinished(false);
    },
    successOnSubmit: () => {
      setFinished(true);
    },
    cart: cartItems.map((item) => ({
      type: "typical",
      extra: false,
      id: item.id,
      item,
      quantity: item.quantity
    })),
    extras: extras.map((item) => ({
      type: "extra",
      extra: true,
      id: item.id,
      item: {
        ...item,
        price_with_discount: calc_price_with_discount(item.discount, item.price)
      },
      quantity: item.quantity
    }))
  });

  const {
    TotalDeposit,
    createOrUpdateOrderRes,
    handleSubmit,
    depositRes,
    depositValue,
    elements,
    full_payment,
    handleChange,
    handleExpressCheckoutElementOnConfirm,
    paymentTypeHandler,
    showContent,
    stripe
  } = usePay;

  const loading = createOrUpdateOrderRes.loading;
  const onlyGift = only_gift(cart);

  const date = moment(orderDetails.time_start).format("YYYY-MM-DD");
  const startTime = moment(orderDetails.time_start).format("HH:mm");

  const endTime = moment(orderDetails.time_end).format("HH:mm");
  const card = elements?.getElement(CardElement) || null;
  const disabledButton =
    !card ||
    loading ||
    disabled ||
    processing ||
    !stripe ||
    !clientSecret ||
    !elements ||
    depositValue === undefined;

  return (
    <div className="checkOutPayment_wrapper">
      <form
        method="POST"
        onSubmit={(e) => {
          e.preventDefault();
          if (disabledButton) {
            return;
          } else {
            handleSubmit(e, CardElement);
          }
        }}
        className="checkout_form checkout_content_wrapper"
      >
        <div className="checkout_left">
          <div>
            <CheckOutPaymentLeft
              depositUnavailable={isDepositAvailable(depositRes.data)}
              DateComp={
                <div className="checkoutAddress_introText">
                  <Raw>
                    {onlyGift
                      ? ""
                      : t(
                          "You have chosen {{date}} for your cleaning from {{start_time}} to {{end_time}}",
                          {
                            "{{date}}": `<span class="med_text">${dateFormat(
                              date,
                              locale
                            )}</span>`,
                            "{{start_time}}": `<span class="med_text">${
                              startTime || "-"
                            }</span>`,
                            "{{end_time}}": `<span class="med_text">${
                              endTime || "-"
                            }</span>.`
                          }
                        )}
                  </Raw>
                </div>
              }
              full_payment={full_payment}
              Total={totalWithCoupon}
              paymentTypeHandler={paymentTypeHandler}
              TotalDeposit={TotalDeposit}
              children={
                showContent() ? (
                  <>
                    <CardElement
                      id="checkout_card_field_id"
                      onChange={handleChange}
                    />
                    {error && <div id="payment-message">{error}</div>}{" "}
                    <ExpressCheckoutElement
                      options={{
                        layout: {
                          maxColumns: 3
                        }
                      }}
                      className="express_checkout"
                      onConfirm={handleExpressCheckoutElementOnConfirm}
                    />
                  </>
                ) : (
                  ``
                )
              }
            />
            <PaymentButtons disabled={disabledButton} smallScreen />
          </div>
        </div>
        <div className="checkout_right">
          <CheckOutListReschedule
            buttonJSX={<PaymentButtons disabled={disabledButton} />}
            couponCheck
            cartCouponTotal={totalWithCoupon}
            cart={cart_items}
            gift_services={gift_services}
            extras={extra_items}
            coupon={details.coupon_code}
          />
        </div>
      </form>
    </div>
  );
};

const mapStateToProps = (state: stateType) => ({
  details: state.orderReducer.details,
  cart: state.cartReducer
});

const mapDispatchToProps = (dispatch: any) => ({
  setOrderDetails: (details: orderType) => {
    dispatch(setOrderDetails(details));
  },
  clearCart: (ignoreAnalytics: boolean) => {
    dispatch(clearCart(ignoreAnalytics));
  },
  setEntireCart: (items: cartInitialState) => {
    dispatch(setEntireCart(items));
  }
});

const PaymentDirectForm = connect(
  mapStateToProps,
  mapDispatchToProps
)(PaymentDirectFormCon);

const PaymentButtons = ({
  smallScreen,
  disabled
}: {
  smallScreen?: boolean;
  disabled?: boolean;
}) => {
  const { t } = useContext(LocaleContext);
  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return (
    <div className={`${smallScreen ? `showSm` : ``}`}>
      <div className={`check_list_btn_wrapper direct`}>
        <div className="check_list_btn_inner flex">
          <Button disabled={disabled} type="submit" size="sm" span>
            {t("Pay")}
          </Button>
        </div>
      </div>
    </div>
  );
};

const CheckOutOngoing = () => {
  const { t } = useContext(LocaleContext);
  return (
    <div className=" checkout_left check_out_successful_wrapper center fdCol">
      <div className="top center">
        <span>{t("Payment is processing.")}</span>
      </div>
      <div className="bottom">
        <p className="fontBlue">
          {t(
            "A message will be sent to the customer's email address once the payment is successful"
          )}
        </p>
      </div>
    </div>
  );
};
